'use client'

import { ComponentProps, ReactNode, useState } from 'react'

import { cn } from '@/shared/lib'
import { Carousel } from '@/shared/ui'

type RootProps = ComponentProps<typeof Carousel.Provider> & {
  children: ReactNode
  showDots?: boolean
}

export const Root = ({
  children,
  options = {},
  useAutoPlay,
  showDots = true,
  ...props
}: RootProps) => {
  const [isHovering, setIsHovering] = useState(false)

  const handleMouseEnter = () => {
    setIsHovering(true)
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
  }

  return (
    <div className="max-w-screen min-w-[1128px]">
      <Carousel.Provider
        orientation="horizontal"
        options={{ loop: true, ...options }}
        useAutoPlay={useAutoPlay}
      >
        <Carousel.Root
          className={cn('mx-auto w-fit max-w-full', showDots && 'mb-11.5')}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...props}
        >
          <Carousel.Content className="-ml-5">{children}</Carousel.Content>
          <div className="absolute top-1/2 left-1/2 w-[1080px] -translate-x-1/2 -translate-y-1/2">
            <Carousel.Previous
              className={cn(
                'left-0 h-12 w-11.5 border-gray-200 opacity-90 shadow-[0_0_7px_rgba(0,0,0,0.25)]',
                'transition-all',
                !isHovering && 'invisible opacity-0',
              )}
            />
            <Carousel.Next
              className={cn(
                'right-0 h-12 w-11.5 border-gray-200 opacity-90 shadow-[0_0_7px_rgba(0,0,0,0.25)]',
                'transition-all',
                !isHovering && 'invisible opacity-0',
              )}
            />
          </div>
          {showDots && (
            <Carousel.DotButton
              className="-bottom-11.5"
              dotClassName="bg-black/50 focus-visible:ring-0"
            />
          )}
        </Carousel.Root>
      </Carousel.Provider>
    </div>
  )
}

export const Item = ({
  children,
  className,
  ...props
}: ComponentProps<typeof Carousel.Item>) => {
  return (
    <Carousel.Item {...props} className={cn('basis-auto pl-5', className)}>
      {children}
    </Carousel.Item>
  )
}
